/**
 * B-spline evaluation using de Boor's algorithm.
 * Used to tessellate SPLINE entities from DXF files.
 */

export interface Point3 {
  x: number;
  y: number;
  z: number;
}

/**
 * Evaluate a B-spline curve at parameter t using de Boor's algorithm.
 */
function deBoor(
  degree: number,
  controlPoints: Point3[],
  knots: number[],
  t: number
): Point3 {
  const n = controlPoints.length - 1;

  // Find knot span index
  let k = degree;
  for (let i = degree; i <= n; i++) {
    if (t >= knots[i] && t < knots[i + 1]) {
      k = i;
      break;
    }
  }
  // Handle end of curve
  if (t >= knots[n + 1]) {
    k = n;
  }

  // Initialize with control points
  const d: Point3[] = [];
  for (let j = 0; j <= degree; j++) {
    const idx = Math.max(0, Math.min(n, k - degree + j));
    d[j] = { ...controlPoints[idx] };
  }

  // de Boor recursion
  for (let r = 1; r <= degree; r++) {
    for (let j = degree; j >= r; j--) {
      const i = k - degree + j;
      const ki = knots[i] ?? 0;
      const kipr = knots[i + degree - r + 1] ?? 1;
      const denom = kipr - ki;
      const alpha = denom === 0 ? 0 : (t - ki) / denom;

      d[j] = {
        x: (1 - alpha) * d[j - 1].x + alpha * d[j].x,
        y: (1 - alpha) * d[j - 1].y + alpha * d[j].y,
        z: (1 - alpha) * d[j - 1].z + alpha * d[j].z,
      };
    }
  }

  return d[degree];
}

/**
 * Tessellate a B-spline into a polyline by sampling numSamples points.
 */
export function tessellateSpline(
  degree: number,
  controlPoints: Point3[],
  knots: number[],
  numSamples: number = 200
): Point3[] {
  if (controlPoints.length < 2 || knots.length < 2) return controlPoints;

  const tMin = knots[degree];
  const tMax = knots[knots.length - degree - 1];
  if (tMin >= tMax) return controlPoints;

  const points: Point3[] = [];
  for (let i = 0; i <= numSamples; i++) {
    const t = tMin + (i / numSamples) * (tMax - tMin);
    points.push(deBoor(degree, controlPoints, knots, t));
  }

  return points;
}